import { LucideIcon, Users, FileText, Home, Activity, MessageSquare, Clock, TrendingUp, BarChart3, Zap } from "lucide-react";

export interface WidgetConfig {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  minW: number;
  minH: number;
  defaultW: number;
  defaultH: number;
  component: string;
  category: "statistics" | "analytics" | "activity" | "tools";
  previewColor: string;
}

export const AVAILABLE_WIDGETS: WidgetConfig[] = [
  {
    id: "stat-users",
    title: "Total Users",
    description: "Displays the current count of registered users with real-time updates when new users sign up.",
    icon: Users,
    minW: 1,
    minH: 2,
    defaultW: 1,
    defaultH: 2,
    component: "StatCard",
    category: "statistics",
    previewColor: "from-blue-500/20 to-blue-600/20",
  },
  {
    id: "stat-claims",
    title: "Total Claims",
    description: "Shows the total number of claims in the system with live count updates as claims are submitted.",
    icon: FileText,
    minW: 1,
    minH: 2,
    defaultW: 1,
    defaultH: 2,
    component: "StatCard",
    category: "statistics",
    previewColor: "from-amber-500/20 to-amber-600/20",
  },
  {
    id: "stat-properties",
    title: "Properties",
    description: "Tracks the number of properties registered by landlords with real-time sync.",
    icon: Home,
    minW: 1,
    minH: 2,
    defaultW: 1,
    defaultH: 2,
    component: "StatCard",
    category: "statistics",
    previewColor: "from-green-500/20 to-green-600/20",
  },
  {
    id: "stat-status",
    title: "System Status",
    description: "Monitors overall system health and displays the current operational status.",
    icon: Activity,
    minW: 1,
    minH: 2,
    defaultW: 1,
    defaultH: 2,
    component: "StatCard",
    category: "statistics",
    previewColor: "from-emerald-500/20 to-emerald-600/20",
  },
  {
    id: "support-requests",
    title: "Support Requests",
    description: "Overview of active support tickets including open, pending, and resolved counts with priority breakdown.",
    icon: MessageSquare,
    minW: 1,
    minH: 4,
    defaultW: 1,
    defaultH: 5,
    component: "SupportRequests",
    category: "activity",
    previewColor: "from-purple-500/20 to-purple-600/20",
  },
  {
    id: "response-time",
    title: "Response Time Analytics",
    description: "Analyzes support response times with SLA compliance rates and average resolution metrics.",
    icon: Clock,
    minW: 1,
    minH: 4,
    defaultW: 1,
    defaultH: 5,
    component: "ResponseTimeAnalytics",
    category: "analytics",
    previewColor: "from-cyan-500/20 to-cyan-600/20",
  },
  {
    id: "activity-feed",
    title: "Activity Feed",
    description: "Real-time feed of recent admin actions, user activities, and system events.",
    icon: Activity,
    minW: 1,
    minH: 4,
    defaultW: 1,
    defaultH: 5,
    component: "ActivityFeed",
    category: "activity",
    previewColor: "from-rose-500/20 to-rose-600/20",
  },
  {
    id: "placement-stats",
    title: "Placement Statistics",
    description: "Comprehensive placement metrics including active placements, trends, and average duration analysis.",
    icon: TrendingUp,
    minW: 1,
    minH: 4,
    defaultW: 1,
    defaultH: 5,
    component: "PlacementStats",
    category: "analytics",
    previewColor: "from-indigo-500/20 to-indigo-600/20",
  },
  {
    id: "claims-processing",
    title: "Claims Processing",
    description: "Tracks claims workflow from submission to approval with processing time analytics and status breakdown.",
    icon: BarChart3,
    minW: 1,
    minH: 4,
    defaultW: 1,
    defaultH: 5,
    component: "ClaimsProcessing",
    category: "analytics",
    previewColor: "from-orange-500/20 to-orange-600/20",
  },
  {
    id: "quick-actions",
    title: "Quick Actions",
    description: "Shortcuts to frequently used admin tasks like adding users, creating claims, and managing properties.",
    icon: Zap,
    minW: 1,
    minH: 3,
    defaultW: 1,
    defaultH: 4,
    component: "QuickActions",
    category: "tools",
    previewColor: "from-yellow-500/20 to-yellow-600/20",
  },
];

export const WIDGET_CATEGORIES = [
  { id: "all", label: "All Widgets" },
  { id: "statistics", label: "Statistics" },
  { id: "analytics", label: "Analytics" },
  { id: "activity", label: "Activity" },
  { id: "tools", label: "Tools" },
] as const;

export interface LayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
}

export const DEFAULT_LAYOUT: LayoutItem[] = [
  { i: "stat-users", x: 0, y: 0, w: 1, h: 2, minW: 1, minH: 2 },
  { i: "stat-claims", x: 1, y: 0, w: 1, h: 2, minW: 1, minH: 2 },
  { i: "stat-properties", x: 2, y: 0, w: 1, h: 2, minW: 1, minH: 2 },
  { i: "stat-status", x: 3, y: 0, w: 1, h: 2, minW: 1, minH: 2 },
  { i: "support-requests", x: 0, y: 2, w: 1, h: 5, minW: 1, minH: 4 },
  { i: "response-time", x: 1, y: 2, w: 1, h: 5, minW: 1, minH: 4 },
  { i: "activity-feed", x: 2, y: 2, w: 1, h: 5, minW: 1, minH: 4 },
  { i: "placement-stats", x: 3, y: 2, w: 1, h: 5, minW: 1, minH: 4 },
  { i: "claims-processing", x: 0, y: 7, w: 1, h: 5, minW: 1, minH: 4 },
  { i: "quick-actions", x: 1, y: 7, w: 1, h: 4, minW: 1, minH: 3 },
];

export const STORAGE_KEY = "admin-dashboard-layout";
export const VISIBLE_WIDGETS_KEY = "admin-dashboard-visible-widgets";
